import { PageType } from "../config/page.types";

/**
 * Includes all explicit application routes.
 */
export enum AppRoutes {
	// Auth routes.
	SignIn = "/auth/sign-in",
	Account = "/auth/account",
	ResetPassword = "/auth/recover/complete",

	// Error routes.
	Unauthorized = "/error/unauthorized",
	Forbidden = "/error/forbidden",
	NotFound = "/error/not-found",

	Results = "/results",
	ResultsOverview = "/results/overview",
	ResultsTransport = "/results/transport",
	ResultsTransportGreenrouter = "/results/transport/greenrouter",
	ResultsTransportGreenrouterDetail = "/results/transport/greenrouter/:id",
	ResultsTransportGreenrouterEntityDetail = "/results/entities/:id",
	ResultsTransportThirdParty = "/results/transport/third-party",
	ResultsTransportThirdPartyDetail = "/results/transport/third-party/:id",
	ResultsEntityGreenRouterDetail = "/results/entities/:entityId/transports/greenrouter/:transportId",
	ResultsOperationalUnits = "/results/operational-units",
	ResultsOperationalUnitsGreenRouter = "/results/operational-units/greenrouter",
	ResultOperationalUnitsGreerouterNode = "/results/operational-units/greenrouter/:id",
	ResultOperationalUnitsGreerouterNodeConsumptions = "/results/operational-units/greenrouter/:id/consumptions",
	ResultsOperationalUnitsThirdParty = "/results/operational-units/third-party",
	ResultsOperationalUnitsThirdPartyDetail = "/results/operational-units/third-party/:id",
	ResultsMap = "/results/map",

	DataInsights = "/data-insights",
	DataInsightsDashboardInsights = "/data-insights/dashboard",
	DataInsightsDashboardInsightsDetail = "/data-insights/dashboard/:id",
	DataInsightsHotspots = "/data-insights/hotspots",
	DataInsightsRootCause = "/data-insights/root-cause",
	DataInsightsRootCauseResults = "/data-insights/root-cause/results",

	WhatIf = "/what-if",
	WhatIfSimulations = "/what-if/simulations",
	WhatIfBudgeting = "/what-if/budgeting",
	WhatIfCarbonSnap = "/what-if/carbon-snap",
	WhatIfCarbonSnapResults = "/what-if/carbon-snap/results",

	DataIO = "/data-io",
	DataIOMonitor = "/data-io/monitor",
	DataIOImport = "/data-io/import",
	DataIOAutomatedProcesses = "/data-io/automated-processes",
	DataIODataExport = "/data-io/data-export",

	MasterData = "/master-data",
	MasterDataOperationalUnits = "/master-data/operational-units",
	MasterDataOperationalUnitsAddUnit = "/master-data/operational-units/add-unit",
	MasterDataOperationalUnitsDetail = "/master-data/operational-units/:id",
	MasterDataAssumptions = "/master-data/assumptions",
	MasterDataFleetsVehicles = "/master-data/fleets-vehicles",
	MasterDataNetworksRoutes = "/master-data/networks-routes",
	MasterDataConversionTables = "/master-data/conversion-tables",

	KnowHow = "/know-how",
	KnowHowUserManuals = "/know-how/user-manuals",
	KnowHowKnowledgeBase = "/know-how/knowledge-base",
	KnowHowApiDocumentation = "/know-how/api-documentation",

	PlatformSettings = "/platform-settings",
	PlatformSettingsDataDomains = "/platform-settings/data-domains",
	PlatformSettingsAdmin = "/platform-settings/admin",
	PlatformSettingsApi = "/platform-settings/api",
}

/**
 * TODO: check if still needed
 * Defines routes used to catch unwanted navigations and redirect users to appropriate
 * application routes; these values should be used only in App component.
 */
export enum CatchRedirectAppRoutes {
	Network = "/network",
}

/**
 * GreenRouter UI menu item keys, used to highlight the correct menu item.
 */
export enum AppMenuKey {
	// Defined, but not used to populate the side menu.
	SignIn = "sign in",
	ResetPassword = "reset password",

	// Results section
	Results = "results",
	ResultsOverview = "overview",
	ResultsTransport = "transport",
	ResultsTransportGreenrouter = "greenrouter transport",
	ResultsTransportGreenrouterDetail = "greenrouter detail transport",
	ResultsTransportThirdParty = "third party transport",
	ResultsTransportThirdPartyDetail = "third party detail transport ",
	ResultsOperationalUnits = "operational units",
	ResultsOperationalUnitsGreenRouter = "greenrouter operational units",
	ResultOperationalUnitsGreerouterNode = "greenrouter detail operational units",
	ResultsOperationalUnitsThirdParty = "third party operational units",
	ResultsOperationalUnitsThirdPartyDetail = "third party detail operational units",
	ResultsMap = "maps",

	// Data Insights section
	DataInsights = "data insights",
	DataInsightsDashboardInsights = "dashboard",
	DataInsightsDashboardInsightsDetail = "dashboard detail",
	DataInsightsHotspots = "hotspots",
	DataInsightsRootCause = "root-cause",
	DataInsightsRootCauseResults = "results",

	WhatIf = "what if",
	WhatIfSimulations = "simulations",
	WhatIfBudgeting = "budgeting",
	WhatIfCarbonSnap = "carbon snap",
	WhatIfCarbonSnapResults = "carbon snap results",

	// Data I/O section
	DataIO = "data io",
	DataIOMonitor = "monitor",
	DataIOImport = "import",
	DataIOAutomatedProcesses = "automated processes",
	DataIODataExport = "data export",

	// Master data & data configuration section
	MasterData = "master data",
	MasterDataOperationalUnits = "operational units",
	MasterDataOperationalUnitsAddUnit = "add operative unit",
	MasterDataOperationalUnitsDetail = "detail",
	MasterDataAssumptions = "assumptions",
	MasterDataFleetsVehicles = "fleets and vehicles",
	MasterDataNetworksRoutes = "networks and routes",
	MasterDataConversionTables = "conversion tables",

	// Know how section
	KnowHow = "know how",
	KnowHowUserManuals = "user manuals",
	KnowHowKnowledgeBase = "knowledge base",
	KnowHowApiDocumentation = "api documentation",

	// Platform settings section
	PlatformSettings = "platform settings",
	PlatformSettingsDataDomains = "data domains",
	PlatformSettingsAdmin = "admin",
	PlatformSettingsAdminRoles = "roles",
	PlatformSettingsAdminUsers = "users",
	PlatformSettingsAdminCompany = "company",
	PlatformSettingsApi = "api",

	Account = "account",

	Theme = "theme",
	SignOut = "sign out",

	ArrowDown = "arrow down",
}

/**
 * parametrize accepts a parametric routes and a map of parameters and produces
 * a valid navigation URL.
 *
 * @param route  the input route
 * @param params the map defining params to be used to build the navigation URL
 * @returns      a string representing a valid URL that can be used to navigate in app
 */
export const parametrize = (route: AppRoutes, params: Object): string => {
	let out = `${route}`;
	const keys = Object.keys(params) as Array<keyof typeof params>;
	keys.forEach((k) => {
		out = out.replace(`:${k}`, `${params[k]}`);
	});
	return out;
};

/**
 * setRouteQuery accepts a routes and a map of parameters and returns the same route
 * by adding given query parameters and returning a valid navigation URL.
 *
 * @param route  the input route
 * @param params the map defining params to be used to build the navigation URL
 * @returns      a string representing a valid URL that can be used to navigate in app
 */
export const setRouteQuery = (route: AppRoutes | string, params: Object): string => {
	const keys = Object.keys(params) as Array<keyof typeof params>;
	if (keys.length <= 0) {
		return route;
	}

	const query: Array<string> = [];
	keys.forEach((k) => {
		query.push(`${k}=${params[k]}`);
	});
	return `${route}?${query}`;
};

/**
 * isRouteActive checks whether the given pathname is the route itself or one of its nested pages.
 *
 * @param route    the menu route (e.g. "/results/transport")
 * @param pathname the current location pathname (e.g. "/results/transport/greenrouter")
 * @returns        true if pathname matches route or is nested under it
 */
export const isRouteActive = (route: AppRoutes | string, pathname: string): boolean =>
	pathname === route || pathname.startsWith(`${route}/`);

/**
 * AppMenuSectionItem defines an item of an AppMenuSection.
 */
export type AppMenuSectionItem = {
	/**
	 * The key used to highlight the menu item section if set on the current navigation stack.
	 */
	key: AppMenuKey;

	/**
	 * The item route.
	 */
	route: AppRoutes | string;

	/**
	 * The PageType associated with this menu entry.
	 * Used for breadcrumbs and navigation purposes, allowing the application to identify
	 * the specific page type associated with this menu item.
	 */
	pageType: PageType;
};

/**
 * AppMenuSection defines one section of the application (side) menu.
 */
export type AppMenuSection = {
	/**
	 * The key used to highlight the menu section if set on the current navigation stack.
	 */
	key: AppMenuKey;

	/**
	 * The base section route; if items is not empty, the menu will never use this route.
	 */
	route?: AppRoutes | string;

	/**
	 * The array of this application section sub-items.
	 */
	items: Array<AppMenuSectionItem>;

	/**
	 * The PageType associated with this menu entry.
	 * Used to reset breadcrumbs.
	 */
	pageType: PageType;
};

/**
 * AppMenuSection defines a section of the application (side) menu.
 */

export const appMenuRoutes: AppMenuSection[] = [
	{
		key: AppMenuKey.Results,
		route: AppRoutes.Results,
		pageType: PageType.ResultsOverview,
		items: [
			{
				key: AppMenuKey.ResultsOverview,
				route: AppRoutes.ResultsOverview,
				pageType: PageType.ResultsOverview,
			},
			{
				key: AppMenuKey.ResultsTransport,
				route: AppRoutes.ResultsTransport,
				pageType: PageType.ResultsTransport,
			},
			{
				key: AppMenuKey.ResultsOperationalUnits,
				route: AppRoutes.ResultsOperationalUnits,
				pageType: PageType.ResultsOperationalUnits,
			},
		],
	},
	{
		key: AppMenuKey.DataInsights,
		route: AppRoutes.DataInsights,
		pageType: PageType.DataInsightsDashboard,
		items: [
			{
				key: AppMenuKey.DataInsightsDashboardInsights,
				route: AppRoutes.DataInsightsDashboardInsights,
				pageType: PageType.DataInsightsDashboard,
			},
			{
				key: AppMenuKey.DataInsightsHotspots,
				route: AppRoutes.DataInsightsHotspots,
				pageType: PageType.DataInsightsHotspots,
			},
			{
				key: AppMenuKey.DataInsightsRootCause,
				route: AppRoutes.DataInsightsRootCause,
				pageType: PageType.DataInsightsRootCause,
			},
		],
	},
	{
		key: AppMenuKey.WhatIf,
		route: AppRoutes.WhatIf,
		pageType: PageType.WhatIfCarbonSnap,
		items: [
			{
				key: AppMenuKey.WhatIfCarbonSnap,
				route: AppRoutes.WhatIfCarbonSnap,
				pageType: PageType.WhatIfCarbonSnap,
			},
			{
				key: AppMenuKey.WhatIfSimulations,
				route: AppRoutes.WhatIfSimulations,
				pageType: PageType.WhatIfSimulations,
			},
			{
				key: AppMenuKey.WhatIfBudgeting,
				route: AppRoutes.WhatIfBudgeting,
				pageType: PageType.WhatIfBudgeting,
			},
		],
	},
	{
		key: AppMenuKey.DataIO,
		route: AppRoutes.DataIO,
		pageType: PageType.DataIOMonitor,
		items: [
			{
				key: AppMenuKey.DataIOMonitor,
				route: AppRoutes.DataIOMonitor,
				pageType: PageType.DataIOMonitor,
			},
			{
				key: AppMenuKey.DataIOImport,
				route: AppRoutes.DataIOImport,
				pageType: PageType.DataIOImport,
			},
			{
				key: AppMenuKey.DataIOAutomatedProcesses,
				route: AppRoutes.DataIOAutomatedProcesses,
				pageType: PageType.DataIOAutomatedProcesses,
			},
			{
				key: AppMenuKey.DataIODataExport,
				route: AppRoutes.DataIODataExport,
				pageType: PageType.DataIODataExport,
			},
		],
	},
	{
		key: AppMenuKey.MasterData,
		route: AppRoutes.MasterData,
		pageType: PageType.MasterDataOperationalUnits,
		items: [
			{
				key: AppMenuKey.MasterDataOperationalUnits,
				route: AppRoutes.MasterDataOperationalUnits,
				pageType: PageType.MasterDataOperationalUnits,
			},
			{
				key: AppMenuKey.MasterDataAssumptions,
				route: AppRoutes.MasterDataAssumptions,
				pageType: PageType.MasterDataAssumptions,
			},
			{
				key: AppMenuKey.MasterDataFleetsVehicles,
				route: AppRoutes.MasterDataFleetsVehicles,
				pageType: PageType.MasterDataFleetsVehicles,
			},
			{
				key: AppMenuKey.MasterDataNetworksRoutes,
				route: AppRoutes.MasterDataNetworksRoutes,
				pageType: PageType.MasterDataNetworksRoutes,
			},
			{
				key: AppMenuKey.MasterDataConversionTables,
				route: AppRoutes.MasterDataConversionTables,
				pageType: PageType.MasterDataConversionTables,
			},
		],
	},

	{
		key: AppMenuKey.KnowHow,
		route: AppRoutes.KnowHow,
		pageType: PageType.KnowHowUserManuals,
		items: [
			{
				key: AppMenuKey.KnowHowUserManuals,
				route: AppRoutes.KnowHowUserManuals,
				pageType: PageType.KnowHowUserManuals,
			},
			{
				key: AppMenuKey.KnowHowKnowledgeBase,
				route: AppRoutes.KnowHowKnowledgeBase,
				pageType: PageType.KnowHowKnowledgeBase,
			},
			{
				key: AppMenuKey.KnowHowApiDocumentation,
				route: AppRoutes.KnowHowApiDocumentation,
				pageType: PageType.KnowHowApiDocumentation,
			},
		],
	},
	{
		key: AppMenuKey.PlatformSettings,
		route: AppRoutes.PlatformSettings,
		pageType: PageType.PlatformSettingsDataDomains,
		items: [
			{
				key: AppMenuKey.PlatformSettingsDataDomains,
				route: AppRoutes.PlatformSettingsDataDomains,
				pageType: PageType.PlatformSettingsDataDomains,
			},
			{
				key: AppMenuKey.PlatformSettingsAdmin,
				route: AppRoutes.PlatformSettingsAdmin,
				pageType: PageType.PlatformSettingsAdmin,
			},
			{
				key: AppMenuKey.PlatformSettingsApi,
				route: AppRoutes.PlatformSettingsApi,
				pageType: PageType.PlatformSettingsApi,
			},
		],
	},
];
